// One pass of the offline audio download: fetch what the window wants and the
// store lacks, the priority set first, then delete what the window no longer
// wants. Pure orchestration over an injected store and download.

import type { AudioAsset } from "@aybuben/core/content/assets.ts";

import { DownloadError, type AudioStore, type Download } from "./types.ts";

/** Network failures in a row that end a pass: one timeout on a flaky link should not. */
export const NETWORK_STREAK = 3;

export type SyncOutcome = "complete" | "network" | "server" | "storage";

export interface SyncReport {
	readonly outcome: SyncOutcome;
	/** Store keys present when the pass ended. */
	readonly stored: ReadonlySet<string>;
	readonly downloaded: number;
	/** Files skipped for being served wrong (`http`, `integrity`). */
	readonly failed: number;
	/** Downloads lost to the connection, whether or not they ended the pass. */
	readonly networkFailures: number;
	/** Store keys of those downloads. */
	readonly networkFailed: ReadonlySet<string>;
	readonly bytes: number;
}

export interface SyncInput {
	readonly wanted: readonly AudioAsset[];
	/** Downloaded first. Always kept, whether or not `wanted` lists it. */
	readonly priority: readonly AudioAsset[];
	readonly store: AudioStore;
	readonly download: Download;
	/** False until progress folds the whole history: a guessed window must not delete. */
	readonly allowCleanup: boolean;
	readonly concurrency?: { readonly priority: number; readonly rest: number };
	/** Fires once the priority downloads settle, whatever the outcome: the launch screen waits on it. */
	readonly onPriorityDone?: (stored: ReadonlySet<string>) => void;
	readonly onStored?: (key: string) => void;
	/** A file the server served wrong. One file, not a reason to stop. */
	readonly onFailed?: (a: AudioAsset, e: DownloadError) => void;
}

/** Runs `fn` over `items`, at most `limit` at once, starting no new item once `halted()`. */
async function eachLimit<T>(
	items: readonly T[],
	limit: number,
	fn: (x: T) => Promise<void>,
	halted: () => boolean,
): Promise<void> {
	let next = 0;
	const worker = async (): Promise<void> => {
		while (next < items.length && !halted()) {
			const i = next++;
			await fn(items[i]!); // i < items.length, checked by the loop condition
		}
	};
	await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

export async function syncAudio(input: SyncInput): Promise<SyncReport> {
	const { store, download } = input;
	const limits = input.concurrency ?? { priority: 4, rest: 2 };
	const stored = new Set(await store.keys());
	// One object, not `let`s: the workers update it from closures, and
	// TypeScript keeps a `let` narrowed to its initial value after them.
	const run: { stop: SyncOutcome | null; streak: number; network: number } = {
		stop: null,
		streak: 0,
		network: 0,
	};
	const networkFailed = new Set<string>();
	let downloaded = 0;
	let failed = 0;
	let bytes = 0;

	const keep = new Set([...input.priority, ...input.wanted].map((a) => store.key(a)));
	const priorityKeys = new Set(input.priority.map((a) => store.key(a)));
	const missing = (list: readonly AudioAsset[], skip: ReadonlySet<string>): AudioAsset[] => {
		const seen = new Set<string>();
		return list.filter((a) => {
			const k = store.key(a);
			if (stored.has(k) || skip.has(k) || seen.has(k)) return false;
			seen.add(k);
			return true;
		});
	};

	const fetchOne = async (a: AudioAsset): Promise<void> => {
		if (run.stop) return;
		let body;
		try {
			body = await download(a);
		} catch (e) {
			if (e instanceof DownloadError && (e.kind === "http" || e.kind === "integrity")) {
				failed++;
				input.onFailed?.(a, e);
				return;
			}
			if (e instanceof DownloadError && e.kind === "server") {
				run.stop ??= "server";
				return;
			}
			// The connection, or an error nothing classified: both count toward
			// stopping, and a stopped pass deletes nothing.
			run.network++;
			networkFailed.add(store.key(a));
			if (++run.streak >= NETWORK_STREAK) run.stop ??= "network";
			return;
		}
		run.streak = 0;
		if (!(await store.put(a, body))) {
			run.stop ??= "storage";
			return;
		}
		const k = store.key(a);
		stored.add(k);
		downloaded++;
		bytes += body.bytes.length;
		input.onStored?.(k);
	};
	const halted = () => run.stop !== null;

	await eachLimit(missing(input.priority, new Set()), limits.priority, fetchOne, halted);
	input.onPriorityDone?.(stored);
	await eachLimit(missing(input.wanted, priorityKeys), limits.rest, fetchOne, halted);

	// Only a finished pass over a known window may delete: one cut short, or
	// computed from a guessed window, knows too little to say what is unwanted.
	// Deleting last also stores a release's new recordings before its old ones go.
	if (!run.stop && input.allowCleanup && keep.size > 0) {
		for (const k of [...stored]) {
			if (keep.has(k)) continue;
			await store.delete(k);
			stored.delete(k);
		}
	}
	return {
		outcome: run.stop ?? "complete",
		stored,
		downloaded,
		failed,
		networkFailures: run.network,
		networkFailed,
		bytes,
	};
}
